/**
 * Student tracks — the family-facing model for the Drive music library.
 *
 * A `studentTracks` doc is a POINTER set: one student, one song, the files the
 * library holds for that pairing (Reference, Track, Piano, Learning, Sheet,
 * Harmony parts), each carrying a Drive view link. No audio lives in Firestore;
 * the studio decided 2026-09-14 that Drive is both archive and delivery.
 *
 * Vocabulary comes from the library's filename convention
 * (docs/canon/systems.md → Music library): Variant words are studio-facing and
 * terse; the labels here are what a parent or a twelve-year-old reads on a
 * button, so they say what the file IS ("Track (no vocals)"), not what it is
 * called in the folder.
 *
 * `Score` is deliberately NOT family-visible: it is the editable MuseScore
 * source, the studio's working file. The Sheet PDF rendered from it is.
 */

const VARIANT_LABELS = {
	reference: 'Reference (with vocals)',
	track: 'Track (no vocals)',
	// The singer alone, the other half of a vocal removal (kept since
	// 2026-09-28: Anthony, "add isolated vocal tracks in case they need to be
	// played back"). Family-visible, like the Track it was separated from.
	vocals: 'Vocals only',
	piano: 'Piano track',
	learning: 'Learning track (melody)',
	sheet: 'Sheet music (PDF)',
};

const INTERNAL_VARIANTS = new Set(['score']);

const variantKey = (variant) => String(variant || '').trim().toLowerCase();

export const isFamilyVisibleVariant = (variant) => {
	const v = variantKey(variant);
	if (!v || INTERNAL_VARIANTS.has(v)) return false;
	return Boolean(VARIANT_LABELS[v]) || v.startsWith('harmony');
};

/** Button text for one file. Harmony parts keep their part name; a key is
 *  appended only when the file was transposed, matching the filename rule. */
export const trackLabel = (file = {}) => {
	// An admin's rename (fileEdits, applyTrackEdits below) is the one title,
	// shown to staff and families alike (Anthony, 2026-09-29).
	if (file.label) return String(file.label);
	const v = variantKey(file.variant);
	let base = VARIANT_LABELS[v];
	if (!base && v.startsWith('harmony')) base = String(file.variant).trim();
	if (!base) base = String(file.variant || 'Track').trim();
	return file.key ? `${base} · ${file.key}` : base;
};

export const isSheet = (file = {}) => variantKey(file.variant) === 'sheet';

/**
 * A file is REACHABLE when it has a Drive link (the library's archive copy) or
 * a Storage copy we can play and download. Until 2026-09-26 every file came
 * from the Drive library and the Drive link was the test; a track added from
 * the Lesson Dashboard ("Add a track", functions/trackJobs.js) lives only in
 * Storage, and a Drive-only test would have hidden every one of them.
 */
const hasDriveLink = (f) => typeof f.driveUrl === 'string' && /^https:\/\/drive\.google\.com\//.test(f.driveUrl);
// A superseded file is a student's EARLIER saved key (step 4): kept in the
// record and in Storage (Anthony: re-saving renders a new one, it does not
// delete the old), shown nowhere.
const isReachable = (f) => Boolean(f) && !f.superseded && (hasDriveLink(f) || Boolean(playbackUrlFor(f)));
export const driveLinkOf = (f = {}) => (f && hasDriveLink(f) ? f.driveUrl : null);

export const familyVisibleFiles = (files = []) =>
	(Array.isArray(files) ? files : []).filter((f) => isReachable(f) && isFamilyVisibleVariant(f.variant));

/** Files grouped by their Length word ("Cut 60s", "Full", "Cut (refrain)"), in
 *  first-seen order, so a song that has both a full track and a cut reads as two
 *  labelled rows rather than one ambiguous pile of buttons. */
export const groupFilesByLength = (files = []) => {
	const rows = new Map();
	for (const f of familyVisibleFiles(files)) {
		const length = String(f.length || '').trim() || 'Track';
		if (!rows.has(length)) rows.set(length, { length, files: [] });
		rows.get(length).files.push(f);
	}
	return [...rows.values()];
};

const lower = (s) => String(s || '').trim().toLowerCase();
const toMillis = (v) => (v && typeof v.toMillis === 'function' ? v.toMillis() : typeof v === 'number' ? v : 0);

/**
 * Merge track docs into the song groups the Songs page already renders.
 *
 * Match by canonical song id when both sides carry one, else by the lowercased
 * song name — the same two keys groupDeliveredBySong keys on. A song that has
 * tracks but no delivered notes yet still gets a card: for an audition cut that
 * is the normal case, the track usually arrives before the first lesson on it.
 * A song with notes and no tracks is untouched. Group order is preserved and
 * track-only groups are appended by recency of the track doc.
 */
export const mergeTracksIntoGroups = (groups = [], trackDocs = []) => {
	const out = groups.map((g) => ({ ...g, tracks: g.tracks || [] }));
	const byId = new Map();
	const byName = new Map();
	out.forEach((g, i) => {
		if (g.songId) byId.set(g.songId, i);
		if (g.songName) byName.set(lower(g.songName), i);
	});
	const extra = [];
	for (const doc of Array.isArray(trackDocs) ? trackDocs : []) {
		if (!doc) continue;
		const files = familyVisibleFiles(doc.files);
		if (files.length === 0) continue;
		const nameKey = lower(doc.songNameLower || doc.songName);
		let idx = doc.songId != null && byId.has(doc.songId) ? byId.get(doc.songId) : undefined;
		if (idx === undefined && nameKey && byName.has(nameKey)) idx = byName.get(nameKey);
		if (idx !== undefined) {
			out[idx] = { ...out[idx], tracks: [...out[idx].tracks, ...files] };
			continue;
		}
		const group = {
			songId: doc.songId || null,
			songName: doc.songName || doc.songNameLower || 'Untitled',
			focus: [],
			landed: [],
			total: 0,
			lastActivity: toMillis(doc.updatedAt),
			tracks: files,
		};
		extra.push(group);
		if (group.songId) byId.set(group.songId, out.length + extra.length - 1);
		if (nameKey) byName.set(nameKey, out.length + extra.length - 1);
		out.push(group);
		extra.pop();
	}
	return out;
};

/* ---------------------------------------------------------------------------
 * Teacher side (2026-09-16). The family half above answers "what may a parent
 * see and what does the button say"; this half answers "what can a TEACHER
 * play in the room". Same docs, same Drive pointers, different audience.
 * ------------------------------------------------------------------------- */

const AUDIO_VARIANTS = new Set(['reference', 'track', 'vocals', 'piano', 'learning']);

/** Audio the player can stream: the audio Variant words plus any
 *  Harmony part. Sheet (PDF) and Score (.mscz) are files to OPEN, not play. */
export const isPlayable = (file = {}) => {
	const v = variantKey(file.variant);
	return AUDIO_VARIANTS.has(v) || v.startsWith('harmony');
};

const DRIVE_ID_RE = /\/file\/d\/([A-Za-z0-9_-]+)/;
export const driveIdOf = (file = {}) => {
	if (file.driveId) return String(file.driveId);
	const m = DRIVE_ID_RE.exec(String(file.driveUrl || ''));
	return m ? m[1] : null;
};

/**
 * The URL of Drive's own embedded player for one file — what an <iframe> in
 * the track strip loads. Chosen 2026-09-16 after the raw-stream host
 * (drive.usercontent.google.com/download) was found to answer 403 to any
 * browser request carrying `Sec-Fetch-Site: cross-site`, which every
 * cross-origin <audio src> is; the /preview page is served to a cross-site
 * iframe and renders play, waveform, time and seek. The `/view` link in
 * `driveUrl` stays as the "Open in Drive" fallback beside each pill.
 */
export const embedUrlFor = (file = {}) => {
	const id = driveIdOf(file);
	return id ? `https://drive.google.com/file/d/${encodeURIComponent(id)}/preview` : null;
};

/**
 * The URL our own <audio> element plays — the copy the filing routine puts in
 * Firebase Storage (`tracks/<folder-slug>/<file>`), served from our project
 * with byte ranges and no Google session in the loop. Decided 2026-09-16
 * after Drive's embed failed in a signed-in browser with a stale Google
 * session (401 from its stream endpoint, player stuck at 0:00 / 0:00) while
 * playing in incognito: playback must not depend on the viewer's Google
 * state. Only a Storage URL is accepted here; a file not yet uploaded has
 * none and falls back to the embed (embedUrlFor).
 */
export const playbackUrlFor = (file = {}) => {
	const u = typeof file.storageUrl === 'string' ? file.storageUrl : '';
	return /^https:\/\/firebasestorage\.googleapis\.com\//.test(u) ? u : null;
};

/** A stable identity for "which file is playing" — the Drive id (or, for a
 *  portal-made track with no Drive copy, its Storage path), never the array
 *  index, so a re-fetched doc does not restart or mis-highlight a track. */
export const trackKey = (file = {}) => driveIdOf(file) || String(file.storagePath || file.driveUrl || file.name || '');

/** Every reachable file (Drive link or Storage copy), Score included — staff may open the editable
 *  source; the family filter above is the one that hides it. */
export const staffVisibleFiles = (files = []) => (Array.isArray(files) ? files : []).filter(isReachable);

/** Terse studio label for a teacher's pill: the Variant word the file is
 *  named with ("Reference", "Track", "Harmony Alto"), plus the key when
 *  transposed. The family label spells out what the file IS; a teacher
 *  already speaks the convention. */
export const staffTrackLabel = (file = {}) => {
	if (file.label) return String(file.label);
	const base = String(file.variant || 'Track').trim() || 'Track';
	return file.key ? `${base} · ${file.key}` : base;
};

/**
 * The track docs that belong to one song, flattened to files. Match on the
 * canonical `songs` id when both sides carry one, else on the lowercased name
 * — the same two keys mergeTracksIntoGroups uses, so the lesson and the Songs
 * page agree about which cut belongs to which tab. A song may have more than
 * one doc (two library folders for one registry song); their files concatenate.
 */
export const trackDocMatchesSong = (doc, song) => {
	if (!doc || !song) return false;
	const id = song.canonicalSongId || song.songId || null;
	const nameKey = lower(song.name || song.songName);
	const byId = id != null && doc.songId != null && doc.songId === id;
	// Name is a fallback for the UNLINKED side only: when both carry a
	// registry id and they differ, a shared title is two songs ("Home" from
	// two shows), not one — so the name must not join them.
	const bothLinkedDifferently = id != null && doc.songId != null && doc.songId !== id;
	const byName = !byId && !bothLinkedDifferently && Boolean(nameKey) && lower(doc.songNameLower || doc.songName) === nameKey;
	return byId || byName;
};

export const tracksForSong = (trackDocs = [], song = {}) => {
	if (!song) return [];
	const out = [];
	for (const doc of Array.isArray(trackDocs) ? trackDocs : []) {
		if (trackDocMatchesSong(doc, song)) out.push(...staffVisibleFiles(doc.files));
	}
	return out;
};

/**
 * Track docs that NO song on the list claims, each with its staff-visible
 * files — "Tracks on file" (2026-09-24).
 *
 * Tracks and repertoire are separate collections ON PURPOSE (a cut is filed
 * from the Drive library; a song reaches the board when a teacher works on
 * it), so a track filed before its song is on the board is a normal state.
 * Every staff surface used to render tracks ONLY under a matching song, so
 * that state was invisible: Calvin Nelson (no repertoire at all) and Natalie
 * Erdmann (a cut of a song not on her board) each had a filed track no
 * teacher could reach — 2 of the 4 filed at the time. The match is the SAME
 * predicate as tracksForSong, so a doc appears in exactly one place: under
 * its song, or here, never both and never neither.
 */
export const unmatchedTrackDocs = (trackDocs = [], songs = []) => {
	const list = (Array.isArray(songs) ? songs : []).filter(Boolean);
	return (Array.isArray(trackDocs) ? trackDocs : [])
		.filter((doc) => doc && !list.some((song) => trackDocMatchesSong(doc, song)))
		.map((doc) => ({ id: doc.id, songName: doc.songName || 'Untitled', show: doc.show || null, files: staffVisibleFiles(doc.files) }))
		.filter((d) => d.files.length > 0)
		.sort((a, b) => a.songName.localeCompare(b.songName));
};

/**
 * The words of one cut (one Length row): the lyrics the filing routine wrote
 * onto the pointer (scripts/music/cutLyrics.js). Reference and Track of a cut
 * carry the same words; the first file that has them answers. `unverified`
 * is Whisper's hearing alone — a staff surface shows it LABELLED, a family
 * surface must not show it at all.
 */
export const cutLyricsOf = (files = []) => {
	const list = Array.isArray(files) ? files : [];
	const pick = list.find((f) => f && f.lyrics && Array.isArray(f.lyrics.lines) && f.lyrics.lines.length && /^reference$/i.test(String(f.variant || '')))
		|| list.find((f) => f && f.lyrics && Array.isArray(f.lyrics.lines) && f.lyrics.lines.length);
	if (!pick) return null;
	const { method = null, unverified = false, lines, from = null } = pick.lyrics;
	return { method, unverified: Boolean(unverified), lines: lines.map((l) => String(l)), from: from || null };
};

/** The provenance line a teacher reads above the words. */
export const cutLyricsLabel = (lyrics) => {
	if (!lyrics) return '';
	if (lyrics.unverified) return 'Transcribed by ear — unverified, check before trusting';
	// The song's own words, timed against its lyric video and checked, copied
	// onto this recording (2026-10-06, functions/trackJobs.js applySongWords).
	if (lyrics.from === 'song') return "The song's words, timed against its lyric video";
	if (lyrics.method === 'synced') return 'Exact — from time-synced lyrics of this recording';
	if (lyrics.method === 'aligned') return 'Published lyrics, located by ear';
	return 'Lyrics';
};

/** Staff grouping by Length word, first-seen order — the teacher's counterpart
 *  of groupFilesByLength, which filters to the family-visible set. */
export const groupStaffFilesByLength = (files = []) => {
	const rows = new Map();
	for (const f of staffVisibleFiles(files)) {
		const length = String(f.length || '').trim() || 'Track';
		if (!rows.has(length)) rows.set(length, { length, files: [] });
		rows.get(length).files.push(f);
	}
	return [...rows.values()];
};


/**
 * Admin edits to a pointer doc: remove (hide, restorable) and rename
 * (Anthony, 2026-09-29: "a way to remove tracks from a student's profile /
 * lesson dashboard, also a way to edit titles"; admins only, same label for
 * families). They live in TWO TOP-LEVEL FIELDS BESIDE `files`, never inside
 * it, because every writer of `files` replaces the array wholesale: the
 * library seed (`seedStudentTracks.js`, merge per doc), `cutLyrics.js`, the
 * archive and the `fileTrackJob` trigger. A tombstone or a label stored on a
 * file would be erased the next time any of them ran; a sibling top-level
 * field survives all four, since each writes with `{ merge: true }`.
 *
 *   displayName: string | null            the song's name, overriding songName
 *   fileEdits: { <fileEditKey>: {
 *     label: string | null,               the file's title on every surface
 *     removedAt, removedBy               hidden until restored (null = shown)
 *   } }
 *
 * Written one dot path per key (StudentTracksService), never as a map.
 */

/** Stable id of one file inside its doc, safe as a Firestore map key. The
 *  Storage path comes FIRST: a lesson-made file gains a Drive id later, when
 *  the archive copies it into the library, and a key built on the Drive id
 *  would orphan every edit made before that. FNV-1a, so no `.` or `/`. */
export const fileEditKey = (file = {}) => {
	const src = String((file && (file.storagePath || driveIdOf(file) || file.driveUrl || file.name)) || '');
	let h = 0x811c9dc5;
	for (let i = 0; i < src.length; i++) {
		h ^= src.charCodeAt(i);
		h = Math.imul(h, 0x01000193);
	}
	return `f${(h >>> 0).toString(36)}`;
};

/**
 * One raw pointer doc with its admin edits applied — what every reader sees
 * (StudentTracksService runs it on every fetch, staff and family alike):
 * `songName` is the admin's name when set (the original stays on
 * `originalSongName`), each kept file carries its `label`, `editKey` and
 * `docId`, and a removed file leaves `files` for `removedFiles`.
 */
export const applyTrackEdits = (doc = {}) => {
	const edits = doc && doc.fileEdits && typeof doc.fileEdits === 'object' ? doc.fileEdits : {};
	const files = [];
	const removedFiles = [];
	for (const f of Array.isArray(doc.files) ? doc.files : []) {
		if (!f) continue;
		const editKey = fileEditKey(f);
		const e = edits[editKey] || {};
		const label = typeof e.label === 'string' && e.label.trim() ? e.label.trim() : null;
		const out = { ...f, label, editKey, docId: doc.id || null };
		if (e.removedAt) removedFiles.push({ ...out, removedAt: e.removedAt, removedBy: e.removedBy || null });
		else files.push(out);
	}
	const displayName = typeof doc.displayName === 'string' && doc.displayName.trim() ? doc.displayName.trim() : null;
	const songName = displayName || doc.songName || null;
	return {
		...doc,
		originalSongName: doc.songName || null,
		songName,
		songNameLower: displayName ? displayName.toLowerCase() : doc.songNameLower || lower(doc.songName),
		files,
		removedFiles,
	};
};

/** The pointer docs that belong to one song (the tracksForSong match, whole
 *  docs rather than files): what the track editor edits. */
export const trackDocsForSong = (trackDocs = [], song = {}) =>
	song ? (Array.isArray(trackDocs) ? trackDocs : []).filter((doc) => trackDocMatchesSong(doc, song)) : [];

/** Every removed file across a student's docs, newest removal first, for the
 *  profile's "Removed tracks" list. */
export const removedTrackFiles = (trackDocs = []) => {
	const ms = (v) => (v && typeof v.toMillis === 'function' ? v.toMillis() : v instanceof Date ? v.getTime() : 0);
	return (Array.isArray(trackDocs) ? trackDocs : [])
		.flatMap((doc) => (doc.removedFiles || []).map((file) => ({ docId: doc.id, songName: doc.songName || 'Untitled', file })))
		.sort((a, b) => ms(b.file.removedAt) - ms(a.file.removedAt));
};
